import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import AddIcon from '@mui/icons-material/Add';
import { setChapterList, addChapter } from '@modules/chapter';

import api from '@utils/axiosInstance';
import LocalizedPlayer from '../common/LocalizedPlayer';
import ChapList from './ChapList';
import '@resources/assets/styles/chapter.scss';

function ChapEditor({ }) {

    const { uuid, lang } = useParams();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const chapterListRef = useRef(null);

    const chapterList = useSelector(state => state.chapter.chapterList);
    const userToken = useSelector(state => state.organization.userToken);
    const organizationData = useSelector(state => state.organization.organizationData)

    const [isFetched, setIsFetched] = useState(false);
    const [videoDurationTime, setVideoDurationTime] = useState(0);
    const [lastAddedIndex, setLastAddedIndex] = useState(null);

    // 자막 가져오기
    useEffect(() => {
        if (lang && uuid) {
            fetchChapterInfo();
        }
    }, [lang, uuid, userToken, organizationData]);

    const fetchChapterInfo = () => {
        setIsFetched(false);

        const newElement = [
            {
                "index": 0,
                "type": "seeker",
                "start": 0,
                "label": "Chapter 1",
                "thumbnail": "",
                "desc": ""
            }
        ]

        api({
            method: 'post',
            url: '/chapter/getChapter',
            data: {
                tk: userToken,
                uuid,
                ogc: organizationData.organization_code,
                lang
            },
            headers: {
                'Content-Type': 'application/json'
            }
        })
            .then((response) => {
                const responseData = response.data?.data;

                if (responseData && responseData.length > 0) {
                    const jsonString = responseData.replace("/", "");
                    let parsedChap;

                    try {
                        parsedChap = JSON.parse(jsonString);
                    } catch (e) {
                        console.error('Error parsing chapter JSON:', e);
                        dispatch(setChapterList(newElement));
                        setIsFetched(true);
                        return;
                    }

                    if (Array.isArray(parsedChap) && parsedChap.length > 0) {
                        const tmpParsed = parsedChap.map(el => ({
                            ...el,
                            start: el.start // 필요한 구조 유지
                        }));
                        dispatch(setChapterList(tmpParsed));
                    } else {
                        dispatch(setChapterList(newElement));
                    }
                } else {
                    dispatch(setChapterList(newElement));
                }

                setIsFetched(true);
            })
            .catch((error) => {
                console.error('Error fetching chapter:', error);
                dispatch(setChapterList(newElement));
                setIsFetched(true);
            });
    };

    // 챕터 목록이 변경되고 lastAddedIndex가 있을 때 스크롤 처리
    useEffect(() => {
        if (lastAddedIndex !== null && chapterListRef.current) {
            const chapterElements = chapterListRef.current.querySelectorAll('.chapList');
            if (chapterElements && chapterElements[lastAddedIndex]) {
                chapterElements[lastAddedIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
                setLastAddedIndex(null); // 스크롤 처리 후 인덱스 초기화
            }
        }
    }, [chapterList, lastAddedIndex]);

    function addChapterBlock(idx) {
        var maxId = Math.max(...chapterList.map(el => Number(el.index)));
        console.log('maxId', maxId, chapterList);
        const prevStart = chapterList[idx].start + 1;
        const newStart = Math.min(prevStart + 1, videoDurationTime);

        const newElement = {
            index: maxId + 1,
            type: "seeker",
            start: newStart,
            label: '',
            thumbnail: '',
            desc: ''
        };

        // Redux 액션 디스패치
        dispatch(addChapter(idx, newElement));

        // API 호출로 서버에 저장
        const newChapters = [
            ...chapterList.slice(0, idx + 1),
            newElement,
            ...chapterList.slice(idx + 1)
        ];

        saveChaptersToServer(newChapters);

        // 추가된 자막의 인덱스 저장 (idx + 1이 새로 추가된 자막의 위치)
        setLastAddedIndex(idx + 1);
    }

    const saveChaptersToServer = (chapArr) => {

        var formatted = chapArr.map(list => Object.assign({
            index: String(list.id + 1),
            type: "seeker",
            start: list.start,
            label: list.label,
            thumbnail: list.thumbnail,
            desc: list.desc
        }))

        const data = {
            tk: userToken,
            uuid,
            ogc: organizationData.organization_code,
            lang,
            chapter: formatted
        };

        api({
            method: 'post',
            url: '/chapter/aUpdate',
            data
        })
            .then(res => {
                console.log('edit data', res.data?.data);
            })
            .catch(error => {
                console.error('Error updating chapter:', error);
            });
    }

    return (
        isFetched ? (
            <div className="container-chapter-editor">
                <div className="wrapper-chapter-editor">
                    <div className="content-chapter-editor">
                        <div className="chapter-editor-layout">
                            <div className="col-chapter">
                                <div className="box-add-chapter">
                                    <IconButton className="btn-add-chapter" onClick={() => addChapterBlock(chapterList.length - 1)}>
                                        <AddIcon fontSize="small" />
                                        <Typography variant="body2">{t("vod.manage.chapter") || "챕터"}</Typography>
                                    </IconButton>
                                </div>

                                <div className="lists-chapter" ref={chapterListRef}>
                                    {chapterList.length > 0 ? (
                                        chapterList.map((el, idx) => (
                                            <ChapList key={`chapter-${el.start}-${el.end}`} idx={idx} item={el} videoDurationTime={videoDurationTime} />
                                        ))
                                    ) : (
                                        <SkeletonChapterPlaceholder />
                                    )}
                                </div>
                            </div>

                            <div className="col-player">
                                <div className="player-wrapper">
                                    {!isFetched ? (
                                        <Skeleton variant="rectangular" width="100%" height="50%" style={{ position: 'absolute', top: '25%' }} />
                                    ) : (
                                        <LocalizedPlayer setVideoDurationTime={setVideoDurationTime} lang={lang} type={"chapter"} />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        ) : (
            <div className="container-chapter-editor">
                <div className="wrapper-chapter-editor">
                    <div className="content-chapter-editor">
                        <div className="chapter-editor-layout">
                            <div className="col-chapter">
                                <div className="lists-chapter" style={{ paddingTop: 50 }} >
                                    <SkeletonChapterPlaceholder />
                                </div>
                            </div>

                            <div className="col-player">
                                <div className="player-wrapper">
                                    <Skeleton variant="rectangular" width="100%" height="50%" style={{ position: 'absolute', top: '25%' }} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    );
}

const SkeletonChapterPlaceholder = () => (
    <div className='chapList' style={{ justifyContent: 'space-between', border: 'none' }}>
        <div className="chapter-number" style={{ border: 'none', height: 80 }}>
            <Skeleton variant="rectangular" width="100%" height="100%" />
        </div>
        <div className="chapter-time" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-around', height: 80 }}>
            <Skeleton variant="rectangular" width="100%" height="35%" />
            <Skeleton variant="rectangular" width="100%" height="35%" />
        </div>
        <div className="chapter-thumbnail" style={{ border: 'none', height: 80 }}>
            <Skeleton variant="rectangular" width="100%" height="100%" />
        </div>
        <div className="chapter-content" style={{ border: 'none', height: 80 }}>
            <Skeleton variant="rectangular" width="100%" height="100%" />
        </div>
    </div>
);
export default ChapEditor;
